import axios from 'axios';
import { authService } from '../auth-service';

// API v1 base URL - all endpoints are versioned under /v1
const API_BASE = import.meta.env?.VITE_API_URL || '/api';
const API_V1_BASE = `${API_BASE}/v1`;

export const api = axios.create({
  baseURL: API_V1_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests (API Gateway authorizer expects ID token)
api.interceptors.request.use(async (config) => {
  const tokens = await authService.getTokens();
  
  if (tokens?.idToken) {
    config.headers.Authorization = `Bearer ${tokens.idToken}`;
  }
  return config;
});

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      try {
        await authService.refreshTokens();
        const tokens = await authService.getTokens();
        if (tokens?.idToken) {
          error.config.headers.Authorization = `Bearer ${tokens.idToken}`;
          return api.request(error.config);
        }
      } catch (_refreshError) {
        // Refresh failed, redirect to login
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// Centralized API Error Handling
// ============================================================================

/**
 * Standard API error class with structured fields for consistent error handling.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number | undefined;
  readonly userMessage: string;

  constructor(message: string, code: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.userMessage = message;
  }
}

/**
 * Extract a user-friendly error message from any error shape.
 * Use this in catch blocks to get a consistent message string.
 */
export function getApiErrorMessage(error: unknown, context?: string): string {
  let message: string;

  if (error instanceof ApiError) {
    message = error.userMessage;
  } else if (axios.isAxiosError(error)) {
    message = error.response?.data?.message
      || error.response?.data?.error
      || error.message
      || 'An unexpected error occurred';
  } else if (error instanceof Error) {
    message = error.message;
  } else {
    message = 'An unexpected error occurred';
  }

  return context ? `${context}: ${message}` : message;
}

/**
 * Parse an API error into a structured ApiError.
 * Use this when you need both the message and metadata (status, code).
 */
export function parseApiError(error: unknown, context?: string): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  let message: string;
  let code: string;
  let status: number | undefined;

  if (axios.isAxiosError(error)) {
    message = error.response?.data?.message
      || error.response?.data?.error
      || error.message
      || 'An unexpected error occurred';
    code = error.response?.data?.code || 'API_ERROR';
    status = error.response?.status;
  } else if (error instanceof Error) {
    message = error.message;
    code = 'UNKNOWN_ERROR';
  } else {
    message = 'An unexpected error occurred';
    code = 'UNKNOWN_ERROR';
  }

  const userMessage = context ? `${context}: ${message}` : message;
  return new ApiError(userMessage, code, status);
}
